import {noop} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {NucleotideType} from 'biojs/BioConstants';
import {LONG_SEQUENCE_NUCLEOTIDE_TYPE_TO_SEQUENCE_SUBTYPE} from 'constants/ModelTypeConstants';
import {SequenceTopology} from 'constants/SequenceConstants';
import FolderSelector, {DEFAULT_PROJECT_FOLDER_LABEL_TEXT} from 'file-browser/FolderSelector';
import {NucleotideTypeToggle} from 'molbio/NucleotideTypeToggle';
import {TopologyDropdown} from 'molbio/TopologyDropdown';
import TagSchemaSelector from 'schemas/shared/TagSchemaSelector';
import StudySearchInput, {StudySearchItem} from 'studies/StudySearchInput';
import FormGroup from 'ui-library/bs/FormGroup';
import {ButtonSize} from 'ui-library/buttons/Button';
import {GRAY_DARKER, GRAY_LIGHT} from 'ui-library/Colors';
import FieldLabel from 'ui-library/inputs/FieldLabel';

import {SequenceImportOptions} from './ImportSequenceConstants';

interface ImportOptionProps<T> {
  isDisabled?: boolean;
  onChange?: (value: T) => void;
  disabledHelpText?: string;
}

interface SequenceImportOptionsProps {
  sequenceImportOptions: SequenceImportOptions;
  folderIdOptions: ImportOptionProps<string | undefined>;
  nucleotideTypeOptions: ImportOptionProps<NucleotideType>;
  studySearchItemOptions?: ImportOptionProps<Array<StudySearchItem> | undefined>;
  tagSchemaIdOptions?: ImportOptionProps<string | undefined>;
  topologyOptions?: ImportOptionProps<SequenceTopology>;
  multipleSequences: boolean;
}

function SequenceImportOptionsForm({
  sequenceImportOptions,
  folderIdOptions,
  nucleotideTypeOptions,
  studySearchItemOptions,
  tagSchemaIdOptions,
  topologyOptions,
  multipleSequences,
}: SequenceImportOptionsProps): JSX.Element {
  const shouldShowStudiesField = BenchlingConfig.STUDIES_ENABLE && studySearchItemOptions != null;
  return (
    <div className={css(optionsStyles.optionsSection)}>
      {multipleSequences && (
        <p className={css(optionsStyles.description)}>These settings apply to all selected sequences.</p>
      )}
      <div className={css(optionsStyles.row)}>
        <div className={css(optionsStyles.item)}>
          <FormGroup>
            <div>
              <FieldLabel label={DEFAULT_PROJECT_FOLDER_LABEL_TEXT} isRequired={true} />
              <FolderSelector
                buttonSize={ButtonSize.MEDIUM}
                value={sequenceImportOptions.folderId}
                noFolderText="Choose folder"
                showInventory={true}
                onChange={({target: {value: folderId}}) => {
                  if (folderId !== sequenceImportOptions.folderId && folderId != null) {
                    folderIdOptions.onChange?.(folderId);
                  }
                }}
                enableRedesign={true}
                unsetOnFetchFail={true}
              />
            </div>
          </FormGroup>
        </div>
        {shouldShowStudiesField && (
          <div className={css(optionsStyles.item)}>
            <FormGroup>
              <div>
                <FieldLabel label="Studies" />
                <StudySearchInput
                  selectedItems={sequenceImportOptions.studySearchItems ?? []}
                  onChange={(studySearchItems: Array<StudySearchItem>) => {
                    studySearchItemOptions.onChange?.(studySearchItems);
                  }}
                  shouldAllowMultiple={true}
                />
              </div>
            </FormGroup>
          </div>
        )}
      </div>
      <div className={css(optionsStyles.row)}>
        <div className={css(optionsStyles.item)}>
          <div>
            <NucleotideTypeToggle
              nucleotideType={sequenceImportOptions.nucleotideType}
              onChangeSelection={nucleotideTypeOptions.onChange ?? noop}
              disabled={nucleotideTypeOptions.isDisabled}
              label="Nucleotide type"
            />
            {nucleotideTypeOptions.isDisabled && (
              <p className={css(optionsStyles.nucleotideTypeHelpText)}>
                {nucleotideTypeOptions.disabledHelpText}
              </p>
            )}
          </div>
        </div>
        <div className={css(optionsStyles.item)}>
          {topologyOptions != null && (
            <div>
              <TopologyDropdown
                label="Topology"
                onChange={(sequenceTopology) => {
                  topologyOptions.onChange?.(sequenceTopology);
                }}
                disabled={topologyOptions.isDisabled}
                topology={sequenceImportOptions.topology}
              />
              {topologyOptions.isDisabled && (
                <p className={css(optionsStyles.topologyHelpText)}>{topologyOptions.disabledHelpText}</p>
              )}
            </div>
          )}
        </div>
      </div>
      <div className={css(optionsStyles.row)}>
        <div className={css(optionsStyles.item)}>
          {tagSchemaIdOptions != null && (
            <FormGroup>
              <div>
                <FieldLabel label="Schema" />
                <TagSchemaSelector
                  allowNone={true}
                  oligoAwareFolderItemTypes={[
                    LONG_SEQUENCE_NUCLEOTIDE_TYPE_TO_SEQUENCE_SUBTYPE[sequenceImportOptions.nucleotideType],
                  ]}
                  onChange={(tagSchema) => {
                    tagSchemaIdOptions.onChange?.(tagSchema ? tagSchema.id : undefined);
                  }}
                  selectedTagSchemaId={sequenceImportOptions.tagSchemaId}
                />
              </div>
            </FormGroup>
          )}
        </div>
      </div>
    </div>
  );
}

const optionsStyles = StyleSheet.create({
  optionsSection: {
    borderTop: `1px solid ${GRAY_LIGHT}`,
    paddingTop: '16px',
  },
  description: {
    color: GRAY_DARKER,
  },
  row: {
    display: 'flex',
    columnGap: '16px',
  },
  item: {
    flex: 1,
  },
  nucleotideTypeHelpText: {
    color: GRAY_DARKER,
    marginTop: '-9px',
  },
  topologyHelpText: {
    color: GRAY_DARKER,
  },
});

export default SequenceImportOptionsForm;
